import PropTypes from "prop-types";
import React, { useEffect, useState } from "react";

import styled from "styled-components/macro";

import { useLoginLevel } from "Flocknote/stateHooks";

import API from "Signups/API.js";
import { getSignedUpViaID } from "Signups/SharedComponents/getSignedUpViaID";
import { getSignupErrorMessage } from "Signups/SharedComponents/getSignupErrorMessage";
import { useIsSignupClosed } from "Signups/SharedComponents/useIsSignupClosed";
import { useSignups } from "Signups/state";
import { useShowPollFields } from "Signups/stateHooks";
import { AnimatePresence, motion } from "framer-motion";

import FnrButton from "UI/FnrButton3";

import isEmptyObject from "Utilities/isEmptyObject";

import CostInfoBadge from "./CostInfoBadge";
import CurrentlySignedUp from "./CurrentlySignedUp";
import Error from "./Error";
import NewUserForm from "./NewUserForm";
import ProgressBarAreaPollType from "./ProgressBarAreaPollType";
import RespondentEntryExtendedFields from "./RespondentEntryExtendedFields";
import {
  SignupItemWrapper,
  Title,
  TitleBar,
  TitleButtonWrapper,
} from "./Styles";
import { useShowNewUserForm } from "./stateHooks";
import { checkIfAnyVoteIsPaidFor } from "./utils/checkIfAnyVoteIsPaidFor";
import { convertExtendedFieldInputResponses } from "./utils/dataTranformations";
import { getExtendedFieldResponsesValidationErrors } from "./utils/fieldValidationHelpers";
import { getUserForSignupSubmission } from "./utils/getUserForSignupSubmission";
import { useIsSignedUpForItem } from "./utils/useIsSignedUpForItem";

SignupItemPollType.propTypes = {
  signupItem: PropTypes.object,
};

export default function SignupItemPollType({ signupItem }) {
  const [state, dispatch] = useSignups();
  const { expandedItemID, extendedFieldInputResponses } = state;
  const { ID, title, extendedFields } = signupItem;
  const isExpanded = expandedItemID === ID;
  const differentItemIsExpanded = expandedItemID && expandedItemID !== ID;
  const [status, setStatus] = useState(null);
  const [showPollFields, setShowPollFields] = useShowPollFields();
  const [signupErrorMessage, setSignupErrorMessage] = useState(null);
  const isCurrentlySignedUp = useIsSignedUpForItem(signupItem.ID);
  const { isClosed } = useIsSignupClosed();

  const multipleVotesAllowed =
    state.currentSignupList.options?.allowMultipleItemSignups === 1;
  const hasPaidForAVote = checkIfAnyVoteIsPaidFor(state.userSignupItems);

  const hideVoteButtonBecauseOfPaidStatus =
    !multipleVotesAllowed && hasPaidForAVote;

  const showNewUserForm = useShowNewUserForm(extendedFields?.length > 0);

  useEffect(() => {
    setStatus(null);
  }, [isExpanded]);

  const hasUser = state.user && !isEmptyObject(state.user);

  const setExpandedItemID = (ID) => {
    dispatch({
      type: "expandedItemID",
      ID,
    });
  };

  const setFieldValidationError = (errors) => {
    dispatch({ type: "setFieldValidationErrors", payload: errors });
  };

  const submitVoteToAPI = async (user) => {
    setFieldValidationError({});

    let respondent = { ...state.user };
    if (!respondent.userID) respondent.userID = respondent.ID;
    const fieldValidationErrors =
      await getExtendedFieldResponsesValidationErrors(
        [respondent],
        signupItem?.extendedFields,
        state?.extendedFieldInputResponses,
        true
      );
    if (fieldValidationErrors) {
      setFieldValidationError(fieldValidationErrors);
      return;
    }
    if (!multipleVotesAllowed) {
      setShowPollFields(false);
    }
    setStatus("loading");

    API.signupForItem2({
      signupItemID: ID,
      user,
      signedUpViaID: getSignedUpViaID(),
      extendedFieldInputResponses: await convertExtendedFieldInputResponses(
        extendedFieldInputResponses,
        extendedFields
      ),
    })
      .post()
      .then((res) => {
        dispatch({
          type: "Item Signup Updated",
          userSignupItems: res.userSignupItems,
          newCounts: res.itemSlotsTaken,
          cancelled: false,
          paymentAmountNeeded: 0, // hides Pay Now while price recalculates
        });
        dispatch({
          type: "updateSlotsTaken",
          payload: {
            signupItemID: ID,
            slotsTaken: res.itemSlotsTaken,
          },
        });

        // don't update the vote counts of other items if multiple votes are allowed
        if (state.currentSignupList.options?.allowMultipleItemSignups === 0) {
          const oldVoteItemID = Object?.keys(state.userSignupItems)[0];
          if (oldVoteItemID) {
            dispatch({
              type: "removeOldVoteFromSlotsTaken",
              payload: { oldVoteItemID: Number(oldVoteItemID) },
            });
          }
        }

        dispatch({
          type: "showConfetti",
          payload: true,
        });

        setExpandedItemID(null);
        setStatus(null);
      })
      .catch((err) => {
        if (err.error === "login_required") {
          // The main use case for "login_required" is if the user is not currently logged in, but have already signed up for this Signup list.
          // forcing them to log in allows them to accurately modify their existing signup.
          window.location = `/login?redirect=signup/${state.currentSignupList.ID}`;
          return;
        }

        setStatus("error");
        const errorCode = err.error;
        setSignupErrorMessage(getSignupErrorMessage(errorCode));

        if (errorCode === "no_user") {
          // this error code is often caused by an invalid email/phone, so we clear the user.
          dispatch({
            type: "user",
            payload: null,
          });
        }
      });
  };

  const handleSubmitClick = () => {
    const user = getUserForSignupSubmission(state);
    submitVoteToAPI(user);
  };

  const handleExpanding = () => setExpandedItemID(ID);

  const renderSignupButton = () => {
    if (
      isCurrentlySignedUp ||
      isClosed ||
      hideVoteButtonBecauseOfPaidStatus ||
      !showPollFields // Do not show vote button without showing the extended field options. If we want to change this, we should then never set showPollFields to false in the api response handler above (setShowPollFields(false))
    )
      return null;

    if (hasUser) {
      return (
        <FnrButton
          color="green"
          onClick={showNewUserForm ? handleExpanding : handleSubmitClick}
          disabled={status === "loading"}
          loading={status === "loading"}
        >
          Vote
        </FnrButton>
      );
    }

    return (
      <FnrButton
        color="blue"
        onClick={handleExpanding}
        disabled={differentItemIsExpanded}
      >
        Login to Vote
      </FnrButton>
    );
  };

  const renderExpandedContent = () => {
    if (showNewUserForm) {
      return <NewUserForm handleCloseClick={() => setExpandedItemID(null)} />;
    }

    return null;
  };

  const showFields =
    showPollFields &&
    hasUser &&
    signupItem?.extendedFields &&
    signupItem?.extendedFields?.length &&
    !showNewUserForm &&
    !isCurrentlySignedUp;

  return (
    <SignupItemWrapper dim={differentItemIsExpanded}>
      <TitleBar>
        <Title>{title}</Title>
        <TitleButtonWrapper>
          {isExpanded ? null : renderSignupButton()}
        </TitleButtonWrapper>
      </TitleBar>

      <AnimatePresence>
        {showFields && (
          <FieldsWrapper>
            <RespondentEntryExtendedFields
              respondent={state.user}
              fields={signupItem.extendedFields}
              signupItem={signupItem}
            />
          </FieldsWrapper>
        )}
      </AnimatePresence>

      <CostInfoBadge
        signupItem={signupItem}
        isCurrentlySignedUp={isCurrentlySignedUp}
      />

      {status === "error" ? (
        <ErrorWrapper>
          <Error message={signupErrorMessage} />
        </ErrorWrapper>
      ) : null}

      <AnimatePresence>
        {isExpanded ? (
          <motion.div
            initial={{ y: -30, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ delay: 0.1 }}
          >
            {renderExpandedContent()}
          </motion.div>
        ) : (
          <>
            {isCurrentlySignedUp ? (
              <CurrentlySignedUp signupItem={signupItem} signupType="poll" />
            ) : null}
            <ProgressBarAreaPollType signupItem={signupItem} />
          </>
        )}
      </AnimatePresence>
    </SignupItemWrapper>
  );
}

const ErrorWrapper = styled.div`
  margin-bottom: 15px;
`;

const FieldsWrapper = styled.div`
  margin-bottom: 28px;
  margin-left: -34px;
`;
